import { Box, Typography } from '@material-ui/core';
import { Button, StandardDialog, SelectField, DateField, TextField } from '@roc/ui';
import { useState, useEffect } from 'react';
import { format as formatDateFns } from 'date-fns';
import { PhotoSourceInfo } from '../services/inspectionService';

const PHOTO_SOURCE_OPTIONS = [
  { label: 'MLS', value: 'MLS' },
  { label: 'Other', value: 'OTHER' },
];

interface SubmitForReviewSourceDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (photoSourceInfo: PhotoSourceInfo) => void;
}

/**
 * Informational only: captured here and shown to the reviewer, but does NOT change the Submit
 * for Review flow itself (same submitInspectionForReview call either way).
 */
export const SubmitForReviewSourceDialog = ({
  open,
  onClose,
  onConfirm,
}: SubmitForReviewSourceDialogProps) => {
  const [photoSource, setPhotoSource] = useState('');
  const [photoSourceOther, setPhotoSourceOther] = useState('');
  const [photosTakenDate, setPhotosTakenDate] = useState<Date | null>(null);

  useEffect(() => {
    if (open) {
      setPhotoSource('');
      setPhotoSourceOther('');
      setPhotosTakenDate(null);
    }
  }, [open]);

  const isOther = photoSource === 'OTHER';
  const isValid =
    !!photoSource &&
    (!isOther || !!photoSourceOther.trim()) &&
    !!photosTakenDate;

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm({
      photoSource,
      photoSourceOther: isOther ? photoSourceOther.trim() : undefined,
      photosTakenDate: formatDateFns(photosTakenDate, 'yyyy-MM-dd'),
    });
    onClose();
  };

  const dialogContent = (
    <Box display="flex" flexDirection="column" gridGap="16px">
      <Typography variant="body2" color="textSecondary">
        Tell us where these photos came from and when they were taken. This is
        for the reviewer's reference only — it doesn't change how your
        submission is processed.
      </Typography>
      <SelectField
        label="Photo Source"
        required
        value={photoSource}
        onChange={value => setPhotoSource(value)}
        options={PHOTO_SOURCE_OPTIONS}
        fullWidth
        variant="outlined"
        testId="photo-source"
      />
      {isOther && (
        <TextField
          label="Please specify"
          required
          value={photoSourceOther}
          onChange={e => setPhotoSourceOther(e.target.value)}
          fullWidth
          variant="outlined"
          testId="photo-source-other"
        />
      )}
      <DateField
        label="Date photos were taken"
        required
        value={photosTakenDate}
        onChange={value => setPhotosTakenDate(value)}
        format="MM/dd/yyyy"
        disableFuture
        maxDate={new Date()}
        fullWidth
        inputVariant="outlined"
        testId="photos-taken-date"
      />
    </Box>
  );

  const dialogActions = (
    <>
      <Button
        variant="outlined"
        onClick={onClose}
        testId="cancel-submit-for-review-source"
        style={{ marginRight: '8px' }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleConfirm}
        disabled={!isValid}
        testId="confirm-submit-for-review-source"
      >
        Confirm
      </Button>
    </>
  );

  return (
    <StandardDialog
      open={open}
      handleClose={onClose}
      title="Submit for Review"
      dialogContent={dialogContent}
      dialogActions={dialogActions}
      maxWidth="xs"
      fullWidth
      removeContentBox
    />
  );
};
